import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Wallet, CheckCircle2, Clock, XCircle, RefreshCcw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import LogCollectionEventModal from "@/components/debtors/LogCollectionEventModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { useRolePermissions } from "@/hooks/useRolePermissions";
import { cn } from "@/lib/utils";

const TYPE_LABEL = {
  payment_received: "Payment",
  settlement: "Settlement",
  wage_garnishment: "Wage Garnishment",
  bank_levy: "Bank Levy",
  other: "Other",
};

const StatusPill = ({ status }) => {
  const map = {
    pending:   { tone: "bg-warning/15 text-warning border-warning/40", label: "Pending" },
    confirmed: { tone: "bg-success/15 text-success border-success/40", label: "Confirmed" },
    disputed:  { tone: "bg-danger/15 text-danger border-danger/40",    label: "Disputed" },
  };
  const cfg = map[status] || map.pending;
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border", cfg.tone)}>
      {cfg.label}
    </span>
  );
};

/**
 * Collection Events section — sits inside Debtor Profile "Overview" tab.
 * Owns its own fetch, modal, and refresh loop.
 */
const CollectionEventsSection = ({ debtor }) => {
  const { canMutateDebtors } = useRolePermissions();
  const [data, setData] = useState({ events: [], total_collected_cents: 0, counts: { pending: 0, confirmed: 0, disputed: 0 } });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    if (!debtor?.id) return;
    try {
      const { data } = await apiClient.get(`/debtors/${debtor.id}/collection-events`);
      setData(data);
    } catch (e) {
      // Non-blocking — the section just stays empty
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [debtor?.id]);

  // Poll while a verification is pending
  useEffect(() => {
    if ((data.counts?.pending || 0) === 0) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [data.counts?.pending, debtor?.id]);

  const events = data.events || [];
  const totalCollected = data.total_collected_cents || 0;
  const disputedEvents = events.filter((e) => e.verification_status === "disputed");

  return (
    <JeaCard data-testid="collection-events-section">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="jea-caption">Recovery Log</div>
          <h3 className="text-white font-semibold text-[16px] mt-1">Collection Events</h3>
          {totalCollected > 0 && (
            <div className="mt-2 flex items-baseline gap-2 flex-wrap" data-testid="collection-total">
              <span className="jea-mono text-success font-extrabold text-[22px]">
                {centsToUSD(totalCollected)}
              </span>
              <span className="text-text-secondary text-[12px]">
                Total Collected · <span className="text-success">{data.counts?.confirmed || 0} confirmed</span>
                {(data.counts?.pending || 0) > 0 && (<> · <span className="text-warning">{data.counts?.pending || 0} pending</span></>)}
                {(data.counts?.disputed || 0) > 0 && (<> · <span className="text-danger">{data.counts?.disputed || 0} disputed</span></>)}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="w-9 h-9 rounded-[8px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/50 inline-flex items-center justify-center transition-colors"
            title="Refresh"
            data-testid="collection-refresh"
          >
            <RefreshCcw size={13} />
          </button>
          {canMutateDebtors && (
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="h-9 px-3 rounded-[8px] border border-accent-pink text-accent-pink hover:bg-accent-pink hover:text-[#091424] text-[13px] font-semibold inline-flex items-center gap-2 transition-colors"
              data-testid="log-collection-event-btn"
            >
              <Plus size={13} /> Log Collection Event
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="rounded-[8px] bg-bg-primary border border-border-default p-6 text-center">
          <Wallet size={20} className="mx-auto text-text-muted mb-2" />
          <div className="text-white text-[14px] font-semibold">No collection events yet</div>
          <p className="text-text-secondary text-[12px] mt-1">Log a payment to start tracking your recovery.</p>
        </div>
      ) : (
        <>
          {disputedEvents.length > 0 && (
            <div className="mb-3 rounded-[8px] bg-danger/5 border border-danger/40 p-3 flex items-start gap-2" data-testid="collection-disputed-banner">
              <AlertTriangle size={14} className="text-danger shrink-0 mt-[3px]" />
              <div className="text-[12px] text-text-secondary leading-5">
                <strong className="text-white">{disputedEvents.length}</strong> event{disputedEvents.length === 1 ? " was" : "s were"} disputed by the platform admin.
                Review the admin note below and reach out to support with your source documentation.
              </div>
            </div>
          )}
          <div className="space-y-2">
            {events.map((e) => (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                className="p-3 rounded-[8px] bg-bg-primary border border-border-default"
                data-testid={`collection-event-${e.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-[8px] bg-bg-tertiary border border-border-default flex items-center justify-center shrink-0">
                    {e.verification_status === "confirmed"
                      ? <CheckCircle2 size={16} className="text-success" />
                      : e.verification_status === "disputed"
                      ? <XCircle size={16} className="text-danger" />
                      : <Clock size={16} className="text-warning" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-semibold text-[13px]">{TYPE_LABEL[e.event_type] || e.event_type}</span>
                      <StatusPill status={e.verification_status} />
                    </div>
                    <div className="text-text-secondary text-[11px] jea-date">
                      {shortDate(e.event_date)} · logged {relativeTime(e.created_at)}
                    </div>
                    {e.notes && <div className="text-text-muted text-[12px] mt-1 italic">"{e.notes}"</div>}
                    {e.verification_status === "disputed" && e.admin_notes && (
                      <div className="text-danger text-[12px] mt-2 p-2 rounded-[6px] bg-danger/5 border border-danger/30">
                        <strong>Admin note:</strong> {e.admin_notes}
                      </div>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="jea-mono text-accent-pink font-bold text-[14px]">{centsToUSD(e.amount_reported_cents)}</div>
                    {e.broker_verified_amount_cents !== null && e.broker_verified_amount_cents !== undefined && (
                      <div className="jea-mono text-text-muted text-[11px]">
                        Verified: {centsToUSD(e.broker_verified_amount_cents)}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </>
      )}

      <LogCollectionEventModal
        open={modalOpen}
        debtor={debtor}
        onClose={() => setModalOpen(false)}
        onLogged={() => load()}
      />
    </JeaCard>
  );
};

export default CollectionEventsSection;
